import { supabase } from '../lib/supabase';

/**
 * Problem Statement Service
 * Handles all problem statement operations with Supabase
 */

const problemStatementService = {
  /**
   * Get all problem statements for current user
   */
  async getUserProblemStatements(userId) {
    try {
      const { data, error } = await supabase?.from('problem_statements')?.select(`
          *,
          assigned_team:teams(id, team_name, team_number)
        `)?.eq('submitted_by', userId)?.order('created_at', { ascending: false });

      if (error) throw error;

      // Convert snake_case to camelCase
      return data?.map(problem => ({
        id: problem?.id,
        submittedBy: problem?.submitted_by,
        title: problem?.title,
        category: problem?.category,
        customCategory: problem?.custom_category,
        priority: problem?.priority,
        complexity: problem?.complexity,
        description: problem?.description,
        background: problem?.background,
        affectedPopulation: problem?.affected_population,
        currentImpact: problem?.current_impact,
        objectives: problem?.objectives,
        constraints: problem?.constraints,
        expectedDeliverables: problem?.expected_deliverables,
        successMetrics: problem?.success_metrics,
        budgetRange: problem?.budget_range,
        resourceAvailability: problem?.resource_availability,
        primaryContact: problem?.primary_contact,
        stakeholders: problem?.stakeholders,
        communicationPreferences: problem?.communication_preferences,
        meetingFrequency: problem?.meeting_frequency,
        preferredMeetingTime: problem?.preferred_meeting_time,
        projectDuration: problem?.project_duration,
        urgencyLevel: problem?.urgency_level,
        projectStartDate: problem?.project_start_date,
        projectEndDate: problem?.project_end_date,
        customDuration: problem?.custom_duration,
        milestones: problem?.milestones,
        seasonalConsiderations: problem?.seasonal_considerations,
        flexibilityRequirements: problem?.flexibility_requirements,
        attachments: problem?.attachments,
        status: problem?.status,
        assignedTeamId: problem?.assigned_team_id,
        assignedTeam: problem?.assigned_team,
        currentPhase: problem?.current_phase,
        createdAt: problem?.created_at,
        updatedAt: problem?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching problem statements:', error);
      throw error;
    }
  },

  /**
   * Create new problem statement
   */
  async createProblemStatement(problemData, userId) {
    try {
      // Convert camelCase to snake_case for database
      const dbData = {
        submitted_by: userId,
        title: problemData?.title,
        category: problemData?.category,
        custom_category: problemData?.customCategory,
        priority: problemData?.priority,
        complexity: problemData?.complexity,
        description: problemData?.description,
        background: problemData?.background,
        affected_population: problemData?.affectedPopulation,
        current_impact: problemData?.currentImpact,
        objectives: problemData?.objectives,
        constraints: problemData?.constraints,
        expected_deliverables: problemData?.expectedDeliverables,
        success_metrics: problemData?.successMetrics,
        budget_range: problemData?.budgetRange,
        resource_availability: problemData?.resourceAvailability,
        primary_contact: problemData?.primaryContact,
        stakeholders: problemData?.stakeholders,
        communication_preferences: problemData?.communicationPreferences,
        meeting_frequency: problemData?.meetingFrequency,
        preferred_meeting_time: problemData?.preferredMeetingTime,
        project_duration: problemData?.projectDuration,
        urgency_level: problemData?.urgencyLevel,
        project_start_date: problemData?.projectStartDate || null,
        project_end_date: problemData?.projectEndDate || null,
        custom_duration: problemData?.customDuration,
        milestones: problemData?.milestones,
        seasonal_considerations: problemData?.seasonalConsiderations,
        flexibility_requirements: problemData?.flexibilityRequirements,
        attachments: problemData?.attachments,
        status: 'pending' // Always default to pending on creation
      };

      const { data, error } = await supabase?.from('problem_statements')?.insert([dbData])?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        status: data?.status,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating problem statement:', error);
      throw error;
    }
  },

  /**
   * Update existing problem statement
   */
  async updateProblemStatement(problemId, problemData) {
    try {
      // Convert camelCase to snake_case for database
      const dbData = {
        title: problemData?.title,
        category: problemData?.category,
        custom_category: problemData?.customCategory,
        priority: problemData?.priority,
        complexity: problemData?.complexity,
        description: problemData?.description,
        background: problemData?.background,
        affected_population: problemData?.affectedPopulation,
        current_impact: problemData?.currentImpact,
        objectives: problemData?.objectives,
        constraints: problemData?.constraints,
        expected_deliverables: problemData?.expectedDeliverables,
        success_metrics: problemData?.successMetrics,
        budget_range: problemData?.budgetRange,
        resource_availability: problemData?.resourceAvailability,
        primary_contact: problemData?.primaryContact,
        stakeholders: problemData?.stakeholders,
        communication_preferences: problemData?.communicationPreferences,
        meeting_frequency: problemData?.meetingFrequency,
        preferred_meeting_time: problemData?.preferredMeetingTime,
        project_duration: problemData?.projectDuration,
        urgency_level: problemData?.urgencyLevel,
        project_start_date: problemData?.projectStartDate,
        project_end_date: problemData?.projectEndDate,
        custom_duration: problemData?.customDuration,
        milestones: problemData?.milestones,
        seasonal_considerations: problemData?.seasonalConsiderations,
        flexibility_requirements: problemData?.flexibilityRequirements,
        attachments: problemData?.attachments
      };

      const { data, error } = await supabase?.from('problem_statements')?.update(dbData)?.eq('id', problemId)?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error updating problem statement:', error);
      throw error;
    }
  },

  /**
   * Delete problem statement
   */
  async deleteProblemStatement(problemId) {
    try {
      const { error } = await supabase?.from('problem_statements')?.delete()?.eq('id', problemId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting problem statement:', error);
      throw error;
    }
  },

  /**
   * Get single problem statement by ID
   */
  async getProblemStatementById(problemId) {
    try {
      const { data, error } = await supabase?.from('problem_statements')?.select(`
          *,
          assigned_team:teams(id, team_name, team_number)
        `)?.eq('id', problemId)?.single();

      if (error) throw error;

      // Convert snake_case to camelCase
      return {
        id: data?.id,
        submittedBy: data?.submitted_by,
        title: data?.title,
        category: data?.category,
        customCategory: data?.custom_category,
        priority: data?.priority,
        complexity: data?.complexity,
        description: data?.description,
        background: data?.background,
        affectedPopulation: data?.affected_population,
        currentImpact: data?.current_impact,
        objectives: data?.objectives,
        constraints: data?.constraints,
        expectedDeliverables: data?.expected_deliverables,
        successMetrics: data?.success_metrics,
        budgetRange: data?.budget_range,
        resourceAvailability: data?.resource_availability,
        primaryContact: data?.primary_contact,
        stakeholders: data?.stakeholders,
        communicationPreferences: data?.communication_preferences,
        meetingFrequency: data?.meeting_frequency,
        preferredMeetingTime: data?.preferred_meeting_time,
        projectDuration: data?.project_duration,
        urgencyLevel: data?.urgency_level,
        projectStartDate: data?.project_start_date,
        projectEndDate: data?.project_end_date,
        customDuration: data?.custom_duration,
        milestones: data?.milestones,
        seasonalConsiderations: data?.seasonal_considerations,
        flexibilityRequirements: data?.flexibility_requirements,
        attachments: data?.attachments,
        status: data?.status,
        assignedTeamId: data?.assigned_team_id,
        assignedTeam: data?.assigned_team,
        currentPhase: data?.current_phase,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching problem statement:', error);
      throw error;
    }
  },

  /**
   * Get all problem statements for PCF Admin
   */
  async getAllProblemStatements() {
    try {
      const { data, error } = await supabase?.from('problem_statements')?.select(`
          *,
          submitter:user_profiles!problem_statements_submitted_by_fkey(id, full_name, email, city, department),
          assigned_team:teams(id, team_name, team_number)
        `)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching all problem statements:', error);
      throw error;
    }
  },

  /**
   * Update problem statement status and assigned team
   */
  async updateProblemStatementStatus(problemId, status, assignedTeamId = null) {
    try {
      const updateData = {
        status,
        updated_at: new Date()?.toISOString()
      };

      if (assignedTeamId) {
        updateData.assigned_team_id = assignedTeamId;
      }

      const { data, error } = await supabase?.from('problem_statements')?.update(updateData)?.eq('id', problemId)?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error updating problem statement status:', error);
      throw error;
    }
  }
};

export default problemStatementService;